import React, { useState } from 'react'
import { Modal, Form, Row, Col } from 'react-bootstrap'
import styled from 'styled-components'
import {
  useCreateUserMutation,
  useUpdateUserMutation,
  useUpdateUserProfileMutation,
} from '../../../services/userApi'
import { useGetOrganizationPublicQuery } from '../../../services/orgsApi'
import { CreateUserReq, UpdateUserReq } from '../../../types/api/api-user'
import { User } from '../../../types/models/user'
import { toTitleCase } from '../../../utils'
import { PrimaryButton, OutlineButton } from './styles'
import { UserPlus, Edit3, Eye, EyeOff, Building2, Mail, User as UserIcon, Lock } from 'lucide-react'

const StyledModal = styled(Modal)`
  .modal-dialog {
    max-width: 700px;
  }

  .modal-content {
    background: linear-gradient(145deg, rgba(26, 26, 26, 0.95) 0%, rgba(20, 20, 20, 0.98) 100%);
    border: 1px solid rgba(34, 211, 238, 0.25);
    border-radius: 20px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05),
      0 0 40px rgba(34, 211, 238, 0.2);
    backdrop-filter: blur(20px);
    overflow: hidden;
  }

  .modal-header {
    background: linear-gradient(135deg, rgba(34, 211, 238, 0.15) 0%, rgba(20, 184, 166, 0.1) 100%);
    border-bottom: 1px solid rgba(34, 211, 238, 0.2);
    border-radius: 20px 20px 0 0;
    padding: 1.5rem 2rem;
    backdrop-filter: blur(10px);

    .modal-title {
      color: #ffffff;
      font-size: 1.5rem;
      font-weight: 700;
      display: flex;
      align-items: center;
      gap: 0.75rem;
      background: linear-gradient(135deg, #ffffff 0%, #22d3ee 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .btn-close {
      filter: invert(1) grayscale(100%) brightness(200%);
      opacity: 0.7;
      transition: all 0.2s ease;

      &:hover {
        opacity: 1;
        transform: rotate(90deg);
      }
    }
  }

  .modal-body {
    background: transparent;
    padding: 2rem;
    max-height: 70vh;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: rgba(34, 211, 238, 0.05);
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(34, 211, 238, 0.3);
      border-radius: 4px;

      &:hover {
        background: rgba(34, 211, 238, 0.5);
      }
    }
  }

  .modal-footer {
    background: linear-gradient(135deg, rgba(34, 211, 238, 0.08) 0%, rgba(20, 184, 166, 0.05) 100%);
    border-top: 1px solid rgba(34, 211, 238, 0.2);
    border-radius: 0 0 20px 20px;
    padding: 1.25rem 2rem;
    backdrop-filter: blur(10px);
  }
`

const StyledFormControl = styled(Form.Control)`
  background: rgba(34, 211, 238, 0.05);
  border: 1px solid rgba(34, 211, 238, 0.25);
  color: #ffffff;
  border-radius: 12px;
  padding: 0.875rem 1.25rem;
  transition: all 0.25s ease;
  font-size: 0.95rem;

  &:focus {
    background: rgba(34, 211, 238, 0.1);
    border-color: rgba(34, 211, 238, 0.5);
    color: #ffffff;
    box-shadow: 0 0 0 3px rgba(34, 211, 238, 0.2), 0 4px 12px rgba(34, 211, 238, 0.15);
    transform: translateY(-1px);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }

  &:hover:not(:focus) {
    border-color: rgba(34, 211, 238, 0.35);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const StyledSelect = styled(Form.Select)`
  background: rgba(34, 211, 238, 0.05);
  border: 1px solid rgba(34, 211, 238, 0.25);
  color: #ffffff;
  border-radius: 12px;
  padding: 0.875rem 1.25rem;
  transition: all 0.25s ease;
  font-size: 0.95rem;

  &:focus {
    background: rgba(34, 211, 238, 0.1);
    border-color: rgba(34, 211, 238, 0.5);
    color: #ffffff;
    box-shadow: 0 0 0 3px rgba(34, 211, 238, 0.2), 0 4px 12px rgba(34, 211, 238, 0.15);
    transform: translateY(-1px);
  }

  &:hover:not(:focus) {
    border-color: rgba(34, 211, 238, 0.35);
  }

  option {
    background: rgba(26, 26, 26, 0.98);
    color: #ffffff;
  }
`

const FormLabel = styled(Form.Label)`
  color: rgba(255, 255, 255, 0.85);
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const FormGroupStyled = styled(Form.Group)`
  margin-bottom: 1.5rem;

  .input-group {
    display: flex;
    align-items: stretch;
  }

  .input-group-text {
    background: rgba(34, 211, 238, 0.1);
    border: 1px solid rgba(34, 211, 238, 0.25);
    border-left: none;
    border-radius: 0 12px 12px 0;
    padding: 0 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.25s ease;

    &:hover {
      background: rgba(34, 211, 238, 0.15);
      border-color: rgba(34, 211, 238, 0.4);
    }

    svg {
      color: #22d3ee;
    }
  }
`

const SectionDivider = styled.div`
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(34, 211, 238, 0.3), transparent);
  margin: 1.5rem 0;
`

interface CreateUserModalProps {
  show: boolean
  onHide: () => void
}

interface EditUserModalProps {
  show: boolean
  onHide: () => void
  user: User | null
}

export const CreateUserModal: React.FC<CreateUserModalProps> = ({ show, onHide }) => {
  const [createUser] = useCreateUserMutation()
  const { data: orgsData } = useGetOrganizationPublicQuery()
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [newUserData, setNewUserData] = useState<CreateUserReq>({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    password_confirm: '',
    org_id: '',
  })

  const handleCreateUser = async () => {
    setIsLoading(true)
    try {
      await createUser(newUserData).unwrap()
      onHide()
      setNewUserData({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        password_confirm: '',
        org_id: '',
      })
    } catch (err) {
      console.error('Failed to create user:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setNewUserData({
      username: '',
      email: '',
      first_name: '',
      last_name: '',
      password: '',
      password_confirm: '',
      org_id: '',
    })
    onHide()
  }

  return (
    <StyledModal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <UserPlus size={24} />
          Create New User
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row>
            <Col md={6}>
              <FormGroupStyled>
                <FormLabel>
                  <UserIcon size={16} />
                  Username
                </FormLabel>
                <StyledFormControl
                  type="text"
                  value={newUserData.username}
                  onChange={(e) =>
                    setNewUserData({
                      ...newUserData,
                      username: e.target.value.toLowerCase(),
                    })
                  }
                  placeholder="Enter username"
                  autoComplete="off"
                />
              </FormGroupStyled>
            </Col>
            <Col md={6}>
              <FormGroupStyled>
                <FormLabel>
                  <Mail size={16} />
                  Email
                </FormLabel>
                <StyledFormControl
                  type="email"
                  value={newUserData.email}
                  onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                  placeholder="user@example.com"
                  autoComplete="off"
                />
              </FormGroupStyled>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroupStyled>
                <FormLabel>First Name</FormLabel>
                <StyledFormControl
                  type="text"
                  value={newUserData.first_name}
                  onChange={(e) =>
                    setNewUserData({
                      ...newUserData,
                      first_name: toTitleCase(e.target.value),
                    })
                  }
                  placeholder="John"
                />
              </FormGroupStyled>
            </Col>
            <Col md={6}>
              <FormGroupStyled>
                <FormLabel>Last Name</FormLabel>
                <StyledFormControl
                  type="text"
                  value={newUserData.last_name}
                  onChange={(e) =>
                    setNewUserData({
                      ...newUserData,
                      last_name: toTitleCase(e.target.value),
                    })
                  }
                  placeholder="Doe"
                />
              </FormGroupStyled>
            </Col>
          </Row>

          <FormGroupStyled>
            <FormLabel>
              <Building2 size={16} />
              Organization
            </FormLabel>
            <StyledSelect
              value={newUserData.org_id || ''}
              onChange={(e) =>
                setNewUserData({
                  ...newUserData,
                  org_id: e.target.value || undefined,
                })
              }
            >
              <option value="">Select an organization</option>
              {orgsData && (
                <option key={orgsData.id} value={orgsData.id}>
                  {orgsData.name}
                </option>
              )}
            </StyledSelect>
          </FormGroupStyled>

          <SectionDivider />

          <Row>
            <Col md={6}>
              <FormGroupStyled>
                <FormLabel>
                  <Lock size={16} />
                  Password
                </FormLabel>
                <div className="input-group">
                  <StyledFormControl
                    type={showPassword ? 'text' : 'password'}
                    value={newUserData.password}
                    onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                    placeholder="Enter password"
                    autoComplete="new-password"
                    style={{ borderRight: 'none', borderRadius: '12px 0 0 12px' }}
                  />
                  <button
                    type="button"
                    className="input-group-text"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      background: 'rgba(34, 211, 238, 0.1)',
                      border: '1px solid rgba(34, 211, 238, 0.25)',
                      borderLeft: 'none',
                      borderRadius: '0 12px 12px 0',
                      borderColor: 'rgba(34, 211, 238, 0.25)',
                    }}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </FormGroupStyled>
            </Col>
            <Col md={6}>
              <FormGroupStyled>
                <FormLabel>
                  <Lock size={16} />
                  Confirm Password
                </FormLabel>
                <div className="input-group">
                  <StyledFormControl
                    type={showPassword ? 'text' : 'password'}
                    value={newUserData.password_confirm}
                    onChange={(e) =>
                      setNewUserData({
                        ...newUserData,
                        password_confirm: e.target.value,
                      })
                    }
                    placeholder="Confirm password"
                    autoComplete="new-password"
                    style={{ borderRight: 'none', borderRadius: '12px 0 0 12px' }}
                  />
                  <button
                    type="button"
                    className="input-group-text"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      background: 'rgba(34, 211, 238, 0.1)',
                      border: '1px solid rgba(34, 211, 238, 0.25)',
                      borderLeft: 'none',
                      borderRadius: '0 12px 12px 0',
                      borderColor: 'rgba(34, 211, 238, 0.25)',
                    }}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </FormGroupStyled>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <OutlineButton onClick={handleClose} disabled={isLoading}>
          Cancel
        </OutlineButton>
        <PrimaryButton onClick={handleCreateUser} disabled={isLoading}>
          {isLoading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Creating...
            </>
          ) : (
            <>
              <UserPlus size={18} style={{ marginRight: '0.5rem' }} />
              Create User
            </>
          )}
        </PrimaryButton>
      </Modal.Footer>
    </StyledModal>
  )
}

export const EditUserModal: React.FC<EditUserModalProps> = ({ show, onHide, user }) => {
  const [updateUser] = useUpdateUserMutation()
  const [updateUserProfile] = useUpdateUserProfileMutation()
  const { data: orgsData } = useGetOrganizationPublicQuery()
  const [isLoading, setIsLoading] = useState(false)
  const [editUserData, setEditUserData] = useState<UpdateUserReq>({
    email: '',
    first_name: '',
    last_name: '',
    org_id: '',
  })

  React.useEffect(() => {
    if (user) {
      setEditUserData({
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        org_id: user.org_id,
      })
    }
  }, [user])

  const handleUpdateUser = async () => {
    if (!user) return
    setIsLoading(true)
    try {
      await updateUser({
        id: user.id,
        userData: editUserData,
      }).unwrap()
      onHide()
    } catch (err) {
      console.error('Failed to update user:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleProfileUpload = async (file: File) => {
    if (!user) return
    try {
      await updateUserProfile({ id: user.id, file }).unwrap()
    } catch (err) {
      console.error('Failed to upload profile:', err)
    }
  }

  const handleClose = () => {
    if (user) {
      setEditUserData({
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        org_id: user.org_id,
      })
    }
    onHide()
  }

  return (
    <StyledModal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <Edit3 size={24} />
          Edit User
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <FormGroupStyled>
            <FormLabel>
              <Mail size={16} />
              Email
            </FormLabel>
            <StyledFormControl
              type="email"
              value={editUserData.email}
              onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })}
              placeholder="user@example.com"
            />
          </FormGroupStyled>

          <Row>
            <Col md={6}>
              <FormGroupStyled>
                <FormLabel>First Name</FormLabel>
                <StyledFormControl
                  type="text"
                  value={editUserData.first_name}
                  onChange={(e) => setEditUserData({ ...editUserData, first_name: e.target.value })}
                  placeholder="John"
                />
              </FormGroupStyled>
            </Col>
            <Col md={6}>
              <FormGroupStyled>
                <FormLabel>Last Name</FormLabel>
                <StyledFormControl
                  type="text"
                  value={editUserData.last_name}
                  onChange={(e) => setEditUserData({ ...editUserData, last_name: e.target.value })}
                  placeholder="Doe"
                />
              </FormGroupStyled>
            </Col>
          </Row>

          <FormGroupStyled>
            <FormLabel>
              <Building2 size={16} />
              Organization
            </FormLabel>
            <StyledSelect
              value={editUserData.org_id || ''}
              onChange={(e) =>
                setEditUserData({
                  ...editUserData,
                  org_id: e.target.value || undefined,
                })
              }
            >
              <option value="">Select an organization</option>
              {orgsData && (
                <option key={orgsData.id} value={orgsData.id}>
                  {orgsData.name}
                </option>
              )}
            </StyledSelect>
          </FormGroupStyled>

          <SectionDivider />

          <FormGroupStyled>
            <FormLabel>Profile Picture</FormLabel>
            <StyledFormControl
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = (e.target as HTMLInputElement).files?.[0]
                if (file) {
                  handleProfileUpload(file)
                }
              }}
              style={{
                padding: '0.75rem',
              }}
            />
          </FormGroupStyled>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <OutlineButton onClick={handleClose} disabled={isLoading}>
          Cancel
        </OutlineButton>
        <PrimaryButton onClick={handleUpdateUser} disabled={isLoading}>
          {isLoading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Saving...
            </>
          ) : (
            <>
              <Edit3 size={18} style={{ marginRight: '0.5rem' }} />
              Save Changes
            </>
          )}
        </PrimaryButton>
      </Modal.Footer>
    </StyledModal>
  )
}
